<template>
    <div class="feedback-request-rule">
        <custom-search-resource-select
            :custom-filter="filterFeedbackRequests"
            :value="selectedFeedbackRequest"
            :options="feedbackRequestOptions"
            :loading="loadingResources"
            field="feedbackRequestId"
            popup-type="grid"
            :read-only="readOnly"
            :check-valid-rule="checkValidRule"
            :placeholder="$t('feedbackRequestRule.Select Feedback Request')"
            @input="$emit('input', $event)">
            <template #header="{ toggleMenu, model }">
                <div
                    v-if="model[0]"
                    class="feedback-resource-select__header">
                    <div class="feedback-resource-select__selected-option">
                        <pendo-actions-option
                            v-if="model[0].feedbackRequestId"
                            :option="model[0]"
                            :actions="feedbackRequestAction"
                            @link="feedbackExternalLink(model[0].url)"
                            @click="toggleMenu" />
                    </div>
                </div>
            </template>
            <template #selectedLabel="{ option }">
                <pendo-actions-option
                    v-if="option.feedbackRequestId"
                    :option="filterOptionAside(option)"
                    :actions="[]" />
            </template>
            <template #option="{ option, index }">
                <pendo-actions-option
                    v-if="option.feedbackRequestId"
                    :option="option"
                    :index="index"
                    :actions="feedbackRequestAction"
                    @link="feedbackExternalLink(option.url)" />
            </template>
            <template #noData>
                <div class="no-data-message">
                    {{ $t('feedbackRequestRule.No Feedback Requests found.') }}
                </div>
            </template>
        </custom-search-resource-select>
    </div>
</template>

<script>
import CustomSearchResourceSelect from '@/segments/components/segment-editor-vue/components/custom-search-resource-select.vue';
import { generateFeedbackSSOUrl } from '@/utils/sso.js';
import { mapState } from 'vuex';
import { PendoActionsOption } from '@pendo/components';

export default {
    name: 'FeedbackRequestRule',
    components: {
        CustomSearchResourceSelect,
        PendoActionsOption,
    },
    props: {
        rule: {
            type: Object,
            required: true,
        },
        resources: {
            type: Array,
            required: true,
        },
        loadingResources: {
            type: Boolean,
            default: false,
        },
        checkValidRule: {
            type: Boolean,
            default: false,
        },
        readOnly: {
            type: Boolean,
            default: false,
        },
    },
    emits: ['input'],
    data () {
        return {
            feedbackRequestAction: [],
        };
    },
    computed: {
        ...mapState({
            hosts: (state) => state.config.hosts,
        }),
        feedbackRequestOptions () {
            if (this.loadingResources) return [];
            const feedbackUrl = generateFeedbackSSOUrl('/app/#/case/', this.hosts);
            // we need to filter out duplicate requests that have different appids
            const uniqueRequestIds = {};
            const feedbackRequestOptions = this.resources.reduce((acc, request) => {
                if (!uniqueRequestIds[request.id]) {
                    uniqueRequestIds[request.id] = request.id;

                    acc.push({
                        feedbackRequestId: request.id,
                        name: request.title,
                        url: `${feedbackUrl}${request.id}`,
                        aside: `#${request.id}`,
                        tooltip: {
                            content: request.title,
                        },
                    });
                }

                return acc;
            }, []);

            return feedbackRequestOptions;
        },
        selectedFeedbackRequest () {
            return this.feedbackRequestOptions.find(
                (feedbackRequest) => feedbackRequest.feedbackRequestId === this.rule.feedbackRequestId,
            );
        },
    },
    created () {
        this.feedbackRequestAction = [
            {
                type: 'link',
                tooltip: this.$t('feedbackRequestRule.View in Feedback'),
                icon: {
                    type: 'external-link',
                },
            },
        ];
    },
    methods: {
        feedbackExternalLink (url) {
            window.open(url, '_blank');
        },
        filterOptionAside (option) {
            const { aside, ...filteredOption } = option;

            return filteredOption;
        },
        filterFeedbackRequests ({ query, options }) {
            const normalizedSearch = query.toLowerCase().trim();

            return options.filter((option) => {
                for (const key of ['name', 'aside']) {
                    const normalizedOptionKey = option[key].toString().toLowerCase();

                    if (normalizedOptionKey.includes(normalizedSearch)) {
                        return true;
                    }
                }

                return false;
            });
        },
    },
};
</script>
<style scoped lang="less">
.feedback-resource-select__header {
    height: 40px;
    border-bottom: 1px solid var(--palette-gray-30);
    display: flex;
    align-items: center;

    .feedback-resource-select__selected-option {
        padding: 0 16px;
        flex: 1;
        cursor: pointer;
        width: 100%;

        .pendo-multiselect-option--actions {
            position: static;
        }

        &:hover {
            background-color: var(--palette-gray-20);
        }
    }
}

:deep(.pendo-multiselect-option__label-container) {
    display: flex;

    .pendo-multiselect-option__label {
        overflow: hidden;
        text-overflow: ellipsis;
        flex-shrink: 1;
    }

    .pendo-multiselect-option__aside {
        flex-grow: 1;
        flex-shrink: 0;
        margin-left: 5px;
    }
}

.pendo-multiselect__selected {
    .pendo-multiselect-option--actions {
        position: static;
    }
}
</style>
